import { ethers } from "ethers";
import { EVM_CONFIG } from "../config";
import { MUCHFI_CHIKYU } from "../config/muchfi";
import {
  requestPoolRelayQuote,
  type PoolSwapQuoteBody,
} from "../utils/relayer-client";
import { estimateWithdrawalShape, type PoolRelayQuote } from "./poolRelayQuote";
import type { StoredJoinSplitNote } from "../joinsplit/types";

export async function fetchMuchfiFee(
  notes: StoredJoinSplitNote[],
  token: string,
  outputToken: string,
  amount: bigint,
) {
  if (EVM_CONFIG.CHAIN_ID !== MUCHFI_CHIKYU.chainId)
    throw Error("Wrong MuchFi network");
  const shape = estimateWithdrawalShape(notes, token, amount);
  if (!shape)
    throw Error(
      "Insufficient available private balance. Please deposit more funds.",
    );
  const body: PoolSwapQuoteBody = {
    chainId: MUCHFI_CHIKYU.chainId,
    pool: ethers.getAddress(EVM_CONFIG.CONTRACTS.SHIELDED_POOL_JOINSPLIT),
    kind: "swap",
    token: ethers.getAddress(token),
    outputToken: ethers.getAddress(outputToken),
    ...shape,
  };
  const q = (await requestPoolRelayQuote(body)) as
    | (Partial<Omit<PoolRelayQuote, "request">> & {
        request?: Record<string, unknown>;
      })
    | null;
  if (
    !q ||
    q.version !== 1 ||
    q.network !== "dogeos-chikyu" ||
    !q.request ||
    Object.entries(body).some(([key, value]) => q.request![key] !== value) ||
    typeof q.fee !== "string" ||
    typeof q.minimumFee !== "string" ||
    !/^[1-9][0-9]{0,36}$/.test(q.fee) ||
    !/^[1-9][0-9]{0,36}$/.test(q.minimumFee) ||
    BigInt(q.minimumFee) > BigInt(q.fee) ||
    !ethers.isAddress(q.feeRecipient || "") ||
    q.issuedAt == null ||
    q.expiresAt == null ||
    !Number.isSafeInteger(q.issuedAt) ||
    !Number.isSafeInteger(q.expiresAt) ||
    q.expiresAt <= q.issuedAt ||
    q.expiresAt - q.issuedAt > 120_000
  )
    throw Error("Invalid MuchFi network fee quote");
  const share =
    (amount * BigInt(EVM_CONFIG.JOINSPLIT_V2.RELAY_FEE_BPS)) / 10_000n;
  return {
    fee: BigInt(q.fee) > share ? BigInt(q.fee) : share,
    minimumFee: BigInt(q.minimumFee),
  };
}
